/* Exam UI keeps its draft in the tab and delegates progress/results to the native quiz. */
(function initializeMemoraExamMode(global) {
    'use strict';
    const d = global.document;
    let view = null;
    let setup = null;
    const escape = (value) => escapeHtml(String(value ?? ''));
    const letter = (index) => String.fromCharCode(65 + index);
    const eye = (struck) => struck ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>' : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h16M8 7h8M8 17h8"/></svg>';

    // Flashcard-only records carry a placeholder second choice; they are never exam questions.
    function bank(source) {
        return source.filter((q) => q && !isFlashcardOnlyStudyItem(q)).map((q) => ({
            question: q,
            key: buildQuestionPersistenceKeyFromQuestion(q),
            stem: String(q.q || q.question || ''),
            choices: [...(q.c || q.choices || q.options || q.o || [])].map(String),
            image: String(q.imgUrl || ''),
            // A stored key that was never confirmed loads on a placeholder choice; it is not scored.
            answer: q.answerUnverified ? -1 : getQuestionAnswerIndexForDedup(q),
            explanation: String(q.e || q.explanation || '')
        })).filter((q) => q.stem && q.choices.length > 1);
    }

    function availableBanks() {
        let selectedKeys = null;
        try {
            const saved = JSON.parse(global.sessionStorage.getItem(`memora_exam_selection_v1::${currentSessionId || 'unsaved'}`));
            if (Array.isArray(saved)) selectedKeys = new Set(saved);
        } catch (_error) { /* Fall back to the native quiz selection. */ }
        const seen = new Set();
        const banks = Object.entries(loadedQuestionSets || {}).filter(([, rows]) => {
            if (!Array.isArray(rows) || seen.has(rows)) return false;
            seen.add(rows); return true;
        }).map(([key, rows]) => ({ key, name: key.replace(/\.json$/i, '').replace(/_/g, ' '), items: bank(rows), selected: (selectedKeys || activeQuestionSetKeys).has(key) }));
        if (!banks.some((b) => b.items.length) && questions.length) banks.push({ key: 'current-quiz', name: 'Current quiz', items: bank(questions), selected: true });
        const eligible = banks.filter((b) => b.items.length);
        if (eligible.length && !eligible.some((b) => b.selected)) eligible[0].selected = true;
        return eligible;
    }

    function selectedPool(state) {
        const signatures = new Map();
        const pool = [];
        const groups = state.banks.filter((b) => b.selected).map((b) => b.items.map((q) => {
            const signature = JSON.stringify([q.stem, q.choices, q.answer, q.image]);
            if (!signatures.has(signature)) { signatures.set(signature, pool.length); pool.push(q); }
            return signatures.get(signature);
        }));
        return { pool, groups };
    }

    function shuffle(values) {
        const copy = [...values];
        for (let i = copy.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [copy[i], copy[j]] = [copy[j], copy[i]]; }
        return copy;
    }

    function sample(groups, count) {
        const queues = shuffle(groups.map(shuffle));
        const picked = new Set();
        // Round robin gives each selected quiz a turn; duplicate questions appear only once.
        while (picked.size < count && queues.some((q) => q.length)) {
            for (const queue of queues) {
                while (queue.length && picked.has(queue[queue.length - 1])) queue.pop();
                if (queue.length) picked.add(queue.pop());
                if (picked.size === count) break;
            }
        }
        return shuffle([...picked]);
    }

    function storageKey(items) {
        // Include answer identity and order: a changed bank must never inherit an attempt.
        const identity = JSON.stringify(items.map((q) => [q.key, q.stem, q.choices, q.answer, q.image]));
        let hash = 2166136261;
        for (let i = 0; i < identity.length; i++) hash = Math.imul(hash ^ identity.charCodeAt(i), 16777619);
        return `memora_exam_attempt_v1::${currentSessionId || 'unsaved'}::${hash >>> 0}`;
    }

    function restore(key, items) {
        try {
            const saved = JSON.parse(global.sessionStorage.getItem(key));
            if (!saved || saved.version !== 2 || !Array.isArray(saved.order) || !saved.order.length || saved.rows?.length !== saved.order.length
                || new Set(saved.order).size !== saved.order.length || !saved.order.every((i) => Number.isInteger(i) && i >= 0 && i < items.length)
                || !saved.rows.every((r, i) => r.key === items[saved.order[i]].key && Array.isArray(r.struck) && Array.isArray(r.highlights)
                    && (r.selected === null || (Number.isInteger(r.selected) && r.selected >= 0 && r.selected < items[saved.order[i]].choices.length)))
                || !Number.isInteger(saved.current) || saved.current < 0 || saved.current >= saved.order.length
                || (saved.deadline !== null && !Number.isFinite(saved.deadline))) return null;
            return saved;
        } catch (_error) { return null; }
    }

    function save() {
        if (!view?.attempt) return;
        try {
            global.sessionStorage.setItem(view.key, JSON.stringify(view.attempt));
        } catch (_error) {
            view.root.querySelector('[data-exam-notice]').textContent = 'This attempt could not be saved in this tab. Keep this window open.';
        }
    }

    function nativeIndices(items) {
        const indices = new Map();
        questions.forEach((q, i) => {
            const key = buildQuestionPersistenceKeyFromQuestion(q);
            if (!indices.has(key)) indices.set(key, i);
        });
        return items.map((q) => indices.get(q.key));
    }

    function persistNativeProgress() {
        syncQuizProgressFill();
        updateLiveStats();
        markSessionStateMutation();
        saveSessionDebounced();
    }

    function syncNativeAnswers(items, rows) {
        const indices = nativeIndices(items);
        if (indices.some((i) => !Number.isInteger(i))) return false;
        rows.forEach((row, i) => {
            const index = indices[i];
            if (row.selected === null) delete userAnswers[index];
            else userAnswers[index] = row.selected;
            delete adaptiveClearedAnswersBackup[index];
        });
        persistNativeProgress();
        return true;
    }

    function prepareNativeAttempt(items, banks) {
        // Drafts own their question pool; the native practice view changes only on submission.
        const available = new Set(banks.filter(bank => bank.selected).flatMap(bank => bank.items.map(item => item.key)));
        return items.length > 0 && items.every(item => available.has(item.key));
    }

    function adaptiveContext() {
        const progress = withParkedQuestionProgress({ statsData: buildIndexedStateBucketsByQuestionKey(statsData) }, listObservedQuestionKeys());
        const stored = sessionPersistenceBase.sessionId === normalizeSessionId(currentSessionId) ? sessionPersistenceBase.buckets.statsData : {};
        const context = { questions: view.items.map(item => item.question), statsData: {}, userAnswers: {},
            currentIndex: view.attempt.current, historyKeys: view.attempt.historyKeys };
        view.attempt.rows.forEach((row, index) => {
            const histories = progress.observed.has(row.key) ? progress.buckets.statsData[row.key] : stored[row.key];
            context.statsData[index] = (histories || []).flat().concat(row.firstAttempt ? [row.firstAttempt] : []);
            if (row.selected !== null) context.userAnswers[index] = row.selected;
        });
        return context;
    }

    function prepareAdaptiveQuestion(index, random = Math.random) {
        const a = view.attempt;
        if (a.submitted || a.historyKeys.includes(view.items[index]?.key)) return;
        const candidates = view.items.map((item, position) => position)
            .filter(position => position >= index && !a.historyKeys.includes(view.items[position].key));
        const picked = sampleAdaptivePracticeCandidate(candidates, random, adaptiveContext());
        if (picked === null || picked === index) return;
        // Swap unseen slots together; visited rail positions and answer identity stay stable.
        for (const list of [view.items, a.rows, a.order]) [list[index], list[picked]] = [list[picked], list[index]];
    }

    function trackQuestionTime() {
        if (!view?.attempt || view.attempt.submitted) return;
        const now = Date.now();
        const row = view.attempt.rows[view.attempt.current];
        row.duration = (Number(row.duration) || 0) + Math.max(0, now - view.questionStartedAt);
        view.questionStartedAt = now;
    }

    function close() {
        if (!view) return;
        trackQuestionTime();
        save();
        clearInterval(view.interval);
        global.removeEventListener('keydown', onKey, true);
        view.root.close();
        view.root.remove();
        if (view.session === currentSessionId && !view.attempt.submitted) {
            renderQuestion();
        }
        view = null;
    }

    function requestClose() {
        if (!global.MemoraModalCoordinator?.close('exam-mode', 'button')) close();
    }

    function resetSetup() {
        if (!setup) return;
        const state = setup;
        if (state.panel.querySelector('select.is-menu-open')) {
            state.opener?.focus({ preventScroll: true });
            global.MemoraSelectMenus?.close();
        }
        // Keep focus inside the dock while reparenting its items; focusout owns dismissal.
        if (state.menu.contains(d.activeElement)) state.opener?.focus({ preventScroll: true });
        state.rootPage.inert = false;
        state.rootPage.removeAttribute('aria-hidden');
        state.menu.append(...state.rootPage.childNodes);
        state.track.remove();
        state.menu.classList.remove('exam-tools-configuring');
        state.menu.setAttribute('role', 'menu');
        state.menu.removeAttribute('aria-label');
        state.menu.removeEventListener('keydown', state.onKey, true);
        state.observer.disconnect();
        state.portalObserver.disconnect();
        state.dock.removeEventListener('focusout', state.onFocusOut, true);
        setup = null;
        scheduleQuickActionsDockPosition();
    }

    function configure(menu, opener) {
        if (setup) resetSetup();
        const menuStyle = getComputedStyle(menu);
        const pageHeight = menu.clientHeight - parseFloat(menuStyle.paddingTop) - parseFloat(menuStyle.paddingBottom);
        const rootPage = d.createElement('div');
        rootPage.className = 'exam-tools-root';
        rootPage.append(...menu.childNodes);
        const track = d.createElement('div');
        track.className = 'exam-tools-track';
        const panel = d.createElement('div');
        panel.className = 'exam-tools-setup';
        const banks = availableBanks();
        panel.innerHTML = `<div class="exam-tools-heading"><button type="button" data-exam-back aria-label="Back to Tools">←</button><strong>Exam mode</strong><button type="button" data-exam-setup-close aria-label="Close Tools">×</button></div>
            <fieldset><legend>Quizzes in this session</legend><div class="exam-bank-list">${banks.map((b, i) => `<label><input type="checkbox" data-exam-bank="${i}" ${b.selected ? 'checked' : ''}><span class="exam-bank-check" aria-hidden="true">✓</span><span>${escape(b.name)}<small>${b.items.length} questions</small></span></label>`).join('') || '<p>No multiple-choice quizzes yet.</p>'}</div></fieldset>
            <label class="exam-tools-count">Questions<select class="theme-select" data-exam-question-count required></select></label>
            <label class="exam-tools-count">Time<select class="theme-select" data-exam-minutes required>${[15, 30, 45, 60, 90, 120, 150, 180, 240].map(minutes => `<option value="${minutes}" ${minutes === 60 ? 'selected' : ''}>${minutes < 60 ? `${minutes} min` : `${minutes / 60} ${minutes === 60 ? 'hour' : 'hours'}`}</option>`).join('')}<option value="0">Untimed</option></select></label>
            <p data-exam-pool-size></p><p data-exam-validation role="alert"></p><div class="exam-tools-actions"><button type="button" data-exam-start>Start exam</button><button type="button" data-exam-resume hidden>Resume exam</button></div>`;
        track.append(rootPage, panel); menu.append(track);
        menu.classList.add('exam-tools-configuring');
        menu.setAttribute('role', 'dialog'); menu.setAttribute('aria-label', 'Exam mode setup');
        const state = { menu, rootPage, track, panel, banks, opener, session: currentSessionId };
        setup = state;
        track.style.height = `${pageHeight}px`;
        function sync() {
            const { pool } = selectedPool(state);
            const count = panel.querySelector('[data-exam-question-count]');
            const previous = Number(count.value);
            count.dataset.max = String(pool.length);
            count.replaceChildren(...Array.from({ length: pool.length }, (_, i) => new Option(String(i + 1), String(i + 1))));
            count.value = String(previous >= 1 && previous <= pool.length ? previous : pool.length);
            global.MemoraSelectMenus?.enhance(count);
            state.saved = restore(storageKey(pool), pool);
            if (!previous && state.saved) {
                count.value = String(state.saved.order.length);
                const time = panel.querySelector('[data-exam-minutes]');
                if ([...time.options].some((o) => Number(o.value) === state.saved.allocatedMinutes)) time.value = String(state.saved.allocatedMinutes);
            }
            panel.querySelector('[data-exam-resume]').hidden = !state.saved;
            panel.querySelector('[data-exam-start]').disabled = !pool.length;
            panel.querySelector('[data-exam-start]').textContent = state.saved ? 'Restart exam' : 'Start exam';
            panel.querySelector('[data-exam-pool-size]').textContent = `${pool.length} unique questions available`;
            scheduleQuickActionsDockPosition();
        }
        panel.addEventListener('change', (event) => {
            if (event.target.dataset.examBank !== undefined) { banks[Number(event.target.dataset.examBank)].selected = event.target.checked; sync(); }
        });
        panel.addEventListener('keydown', (event) => event.stopPropagation());
        panel.addEventListener('click', (event) => {
            event.stopPropagation();
            const target = event.target.closest('button');
            if (!target) return;
            if (target.matches('[data-exam-back]')) {
                state.opener?.focus({ preventScroll: true });
                track.classList.remove('is-configuring'); panel.inert = true; rootPage.inert = false; rootPage.removeAttribute('aria-hidden');
                global.setTimeout(() => { if (setup === state) resetSetup(); menu.querySelector('[data-quick-action="exam"]')?.focus(); }, global.MemoraMotion?.isReduced?.() ? 0 : 240);
            } else if (target.matches('[data-exam-setup-close]')) { resetSetup(); closeQuickActionsDock(); opener?.focus(); }
            else if (target.matches('[data-exam-start], [data-exam-resume]')) {
                if (state.session !== currentSessionId) { panel.querySelector('[data-exam-validation]').textContent = 'The session changed. Reopen Exam mode to select its quizzes.'; return; }
                const resume = target.matches('[data-exam-resume]');
                const count = panel.querySelector('[data-exam-question-count]');
                const minutes = panel.querySelector('[data-exam-minutes]');
                const timed = Number(minutes.value) > 0;
                if (!resume && (!count.checkValidity() || !minutes.checkValidity())) {
                    panel.querySelector('[data-exam-validation]').textContent = `Choose 1–${count.dataset.max} questions and a time.`; return;
                }
                const { pool, groups } = selectedPool(state);
                const key = storageKey(pool);
                const attempt = resume ? restore(key, pool) : { version: 2,
                    order: state.saved && state.saved.order.length === Number(count.value) ? [...state.saved.order] : sample(groups, Number(count.value)),
                    current: 0, submitted: false, allocatedMinutes: Number(minutes.value),
                    deadline: timed ? Date.now() + Number(minutes.value) * 60000 : null };
                if (!attempt) return;
                const items = attempt.order.map((i) => pool[i]);
                if (!resume) {
                    attempt.historyKeys = [];
                    attempt.rows = items.map((q) => ({ key: q.key, selected: null, flagged: false, struck: [], highlights: [], notes: '', duration: 0 }));
                }
                if (!prepareNativeAttempt(items, banks)) {
                    panel.querySelector('[data-exam-validation]').textContent = 'These quizzes changed. Reopen Exam mode to select their current questions.';
                    return;
                }
                try { global.sessionStorage.setItem(`memora_exam_selection_v1::${currentSessionId || 'unsaved'}`, JSON.stringify(banks.filter(b => b.selected).map(b => b.key))); } catch (_error) { /* The attempt itself reports storage failures. */ }
                resetSetup(); closeQuickActionsDock(); open(items, key, attempt, opener, banks);
            }
        });
        state.onKey = (event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); resetSetup(); menu.querySelector('[data-quick-action="exam"]')?.focus(); } };
        menu.addEventListener('keydown', state.onKey, true);
        state.observer = new MutationObserver(() => { if (menu.hidden && setup === state) resetSetup(); });
        state.observer.observe(menu, { attributes: true, attributeFilter: ['hidden'] });
        // The shared dropdown is portaled to body. Keep its focus inside this Tools flow.
        state.dock = menu.closest('#quiz-quick-actions-dock');
        state.onFocusOut = (event) => {
            if (panel.querySelector('select.is-menu-open') && event.relatedTarget?.closest('#memora-select-menu')) event.stopImmediatePropagation();
        };
        state.dock.addEventListener('focusout', state.onFocusOut, true);
        state.portalObserver = new MutationObserver((records) => {
            if (!panel.querySelector('select.is-menu-open')) return;
            for (const record of records) for (const node of record.addedNodes) {
                if (node.id !== 'memora-select-menu') continue;
                node.addEventListener('click', (event) => event.stopPropagation());
                node.addEventListener('keydown', (event) => event.stopPropagation());
            }
        });
        state.portalObserver.observe(d.body, { childList: true });
        global.MemoraSelectMenus?.enhance(panel.querySelector('[data-exam-minutes]'));
        sync();
        rootPage.inert = true; rootPage.setAttribute('aria-hidden', 'true');
        global.requestAnimationFrame(() => { if (setup === state) { track.classList.add('is-configuring'); panel.querySelector('button').focus(); scheduleQuickActionsDockPosition(); } });
        return true;
    }

    function open(items, key, attempt, opener, banks) {
        if (view || !items.length) return false;
        const root = d.createElement('dialog');
        root.className = 'memora-exam-mode';
        root.setAttribute('role', 'dialog');
        root.setAttribute('aria-modal', 'true');
        root.setAttribute('aria-label', 'Exam mode');
        root.tabIndex = -1;
        view = { root, items, key, attempt, bankKeys: banks.filter(bank => bank.selected).map(bank => bank.key), interval: null, filter: 'all', tool: '', opener, session: currentSessionId, questionStartedAt: Date.now() };
        attempt.historyKeys = Array.isArray(attempt.historyKeys)
            ? normalizeQuizCompletionQuestionKeys(attempt.historyKeys)
            : items.slice(0, attempt.current + 1).map(item => item.key);
        if (!attempt.historyKeys.length) prepareAdaptiveQuestion(attempt.current);
        d.body.appendChild(root);
        root.addEventListener('click', onClick);
        root.addEventListener('pointerup', highlightSelection);
        // Native top layer clears the rail's stacking context and makes the workspace inert.
        root.showModal();
        global.addEventListener('keydown', onKey, true);
        global.MemoraModalCoordinator?.open({ id: 'exam-mode', element: root, opener,
            trapFocus: true, restoreFocus: true, isOpen: () => !!view, close: () => { close(); return true; } });
        start();
        return true;
    }

    function start() {
        view.root.innerHTML = `<header class="exam-header"><div class="exam-brand"><svg class="exam-brand-mark" viewBox="18 20 64 58" role="img" aria-label="Memoraful"><defs><linearGradient id="exam-brand-grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#e7a74f"/><stop offset="100%" stop-color="#d97757"/></linearGradient></defs><path style="stroke:currentColor;stroke-width:3.2;stroke-linecap:square;fill:none" d="M23 26h8M27 26v48M21 74h12M69 26h8M73 26v48M67 74h12"/><path style="stroke:url(#exam-brand-grad);stroke-width:4.2;stroke-linecap:round;stroke-linejoin:round;fill:none" d="M27 26 50 62 73 26"/><path style="fill:#e7a74f;stroke:none" d="M50 54l6.5 8-6.5 8-6.5-8z"/></svg><span>Exam mode</span></div>
            <div class="exam-timer"><span>Time left</span> <strong data-exam-timer></strong></div>
            <details class="exam-menu"><summary><span>Exam</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg></summary><div><button type="button" data-exam-action="submit">Submit exam</button><button type="button" data-exam-action="close">Return to quiz</button></div></details>
            <details class="exam-menu"><summary><span>Tools</span><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="4" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="20" r="1"/></svg></summary><div><button type="button" data-exam-action="highlight" aria-pressed="false">Highlighter</button><button type="button" data-exam-action="erase" aria-pressed="false">Eraser</button><button type="button" data-exam-action="notes" aria-pressed="false">Question notes</button></div></details><button type="button" class="exam-close" data-exam-action="close" aria-label="Close Exam mode and return to quiz" title="Return to quiz">×</button></header>
            <div class="exam-body"><aside class="exam-rail"><details class="exam-filter"><summary>Filter</summary><div>${['all', 'unanswered', 'flagged'].map((f) => `<button type="button" data-exam-filter="${f}" aria-pressed="${f === 'all'}">${f[0].toUpperCase() + f.slice(1)}</button>`).join('')}</div></details><button type="button" data-exam-action="rail-up" aria-label="Scroll questions up">⌃</button><nav class="exam-numbers" aria-label="Exam questions"></nav><button type="button" data-exam-action="rail-down" aria-label="Scroll questions down">⌄</button></aside>
            <main class="exam-main"><div data-exam-summary role="status" hidden></div><section class="exam-question"></section><div class="exam-notes" hidden><label>Question notes<textarea data-exam-notes rows="4"></textarea></label></div><p data-exam-notice role="status"></p></main></div>
            <footer class="exam-footer"><span data-exam-count></span><span class="exam-practice-label">Practice exam</span><div><button type="button" data-exam-action="previous">Previous</button><button type="button" data-exam-action="next">Next</button></div></footer>
            <div class="exam-submit-confirm" role="alertdialog" aria-modal="true" aria-label="Submit exam" hidden><h2>Submit exam?</h2><p data-exam-submit-count></p><button type="button" data-exam-action="cancel-submit">Keep working</button><button type="button" data-exam-action="confirm-submit">Submit exam</button></div>`;
        view.root.querySelector('[data-exam-notes]').addEventListener('input', (event) => {
            tick();
            if (!view || view.attempt.submitted) return;
            view.attempt.rows[view.attempt.current].notes = event.target.value; save();
        });
        render();
        if (view.attempt.submitted) { finishNativeFeedback(); return; }
        tick();
        if (!view) return;
        if (!view.attempt.submitted) view.interval = global.setInterval(tick, 1000);
        save();
    }

    function render() {
        const a = view.attempt;
        const row = a.rows[a.current];
        const q = view.items[a.current];
        if (!a.historyKeys.includes(q.key)) a.historyKeys.push(q.key);
        const scored = view.items.filter((item) => item.answer >= 0 && item.answer < item.choices.length).length;
        view.root.querySelector('[data-exam-count]').textContent = `Question ${a.current + 1} of ${view.items.length}`;
        view.root.querySelector('.exam-numbers').innerHTML = a.rows.map((r, i) => {
            if ((view.filter === 'unanswered' && r.selected !== null) || (view.filter === 'flagged' && !r.flagged)) return '';
            return `<button type="button" data-exam-jump="${i}" class="${r.selected !== null ? 'is-answered' : ''} ${i === a.current ? 'is-current' : ''} ${r.flagged ? 'is-flagged' : ''}" ${i === a.current ? 'aria-current="step"' : ''} aria-label="Question ${i + 1}${r.selected !== null ? ', answered' : ', unanswered'}${r.flagged ? ', flagged' : ''}">${i + 1}</button>`;
        }).join('') || '<small>No matches</small>';
        view.root.querySelector('.exam-question').innerHTML = `<div class="exam-question-heading"><strong tabindex="-1" data-exam-heading>Question ${a.current + 1}</strong><button type="button" class="exam-flag" data-exam-action="flag" aria-pressed="${!!row.flagged}" ${a.submitted ? 'disabled' : ''}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 21V4h11l-2 4 2 4H5"/></svg>${row.flagged ? 'Flagged' : 'Flag'}</button></div>
            <div class="exam-stem" data-exam-field="stem">${formatQuestionStemHtml(q.stem)}</div>
            ${q.image ? '<img class="exam-question-image" alt="Question attachment" hidden>' : ''}
            <p class="exam-selection">${row.selected === null ? 'Choose one answer' : `Your answer: ${letter(row.selected)}`}</p>
            <div class="exam-answers">${q.choices.map((choice, i) => `<div class="exam-answer ${row.selected === i ? 'is-selected' : ''} ${row.struck.includes(i) ? 'is-struck' : ''}"><button type="button" class="exam-answer-select" data-exam-choice="${i}" aria-pressed="${row.selected === i}" ${a.submitted || row.struck.includes(i) ? 'disabled' : ''}><strong>${letter(i)}</strong><span data-exam-field="choice-${i}">${escape(choice.replace(/^[A-F]\.\s*/i, ''))}</span></button><button type="button" class="exam-eye" data-exam-strike="${i}" aria-label="${row.struck.includes(i) ? 'Restore' : 'Strike out'} answer ${letter(i)}" aria-pressed="${row.struck.includes(i)}" ${a.submitted ? 'disabled' : ''}>${row.selected === i ? '<span aria-hidden="true">✓</span>' : eye(row.struck.includes(i))}</button></div>`).join('')}</div>
            ${a.submitted ? `<div class="exam-feedback"><strong>${q.answer < 0 || q.answer >= q.choices.length ? 'No valid answer key available' : `Correct answer: ${letter(q.answer)} · ${row.selected === q.answer ? 'Correct' : row.selected === null ? 'Unanswered' : 'Incorrect'}`}</strong><p>${escape(q.explanation)}</p></div>` : ''}`;
        restoreHighlights(row);
        if (q.image) {
            const image = view.root.querySelector('.exam-question-image');
            getImageBlobUrl(q.image).then((url) => {
                if (!image.isConnected) return;
                if (url && /^(https?:|blob:|data:image\/)/i.test(url)) { image.src = url; image.hidden = false; }
                else image.replaceWith(Object.assign(d.createElement('p'), { textContent: 'Question attachment unavailable.' }));
            }).catch(() => { if (image.isConnected) image.replaceWith(Object.assign(d.createElement('p'), { textContent: 'Question attachment unavailable.' })); });
        }
        view.root.querySelector('[data-exam-notes]').value = row.notes || '';
        view.root.querySelector('[data-exam-notes]').readOnly = !!a.submitted;
        view.root.querySelector('[data-exam-action="previous"]').disabled = a.current === 0;
        view.root.querySelector('[data-exam-action="next"]').textContent = a.current === view.items.length - 1 ? (a.submitted ? 'Close review' : 'Finish') : 'Next';
        view.root.querySelector('[data-exam-action="submit"]').disabled = !!a.submitted;
        const summary = view.root.querySelector('[data-exam-summary]');
        summary.hidden = !a.submitted;
        if (a.submitted) {
            const correct = a.rows.filter((r, i) => r.selected !== null && view.items[i].answer >= 0 && r.selected === view.items[i].answer).length;
            summary.textContent = `${a.expired ? 'Time is up. ' : ''}Exam submitted · ${correct} / ${scored} correct · ${a.rows.filter((r) => r.selected === null).length} unanswered. Review with the question rail.`;
        }
        save();
    }

    function navigate(index) {
        if (index < 0 || index >= view.items.length || index === view.attempt.current) return;
        trackQuestionTime();
        if (index === view.attempt.current + 1) prepareAdaptiveQuestion(index);
        view.attempt.current = index;
        render();
        view.root.querySelector('.exam-main').scrollTop = 0;
        view.root.querySelector('[data-exam-heading]').focus({ preventScroll: true });
        view.root.querySelector('[aria-current="step"]')?.scrollIntoView({ block: 'nearest' });
        if (!global.matchMedia('(prefers-reduced-motion: reduce)').matches && !d.body.classList.contains('reduce-motion') && !global.MemoraMotion?.isReduced?.()) {
            view.root.querySelector('.exam-question').animate([{ opacity: 0, transform: 'translateX(16px)' }, { opacity: 1, transform: 'translateX(0)' }], { duration: 140 });
        }
    }

    function tick() {
        if (!view?.attempt) return;
        const a = view.attempt;
        const remaining = a.deadline === null ? null : Math.max(0, Math.ceil((a.deadline - Date.now()) / 1000));
        const timer = view.root.querySelector('[data-exam-timer]');
        timer.textContent = a.submitted ? 'SUBMITTED' : remaining === null ? 'UNTIMED' : `${remaining >= 3600 ? Math.floor(remaining / 3600).toString().padStart(2, '0') + ':' : ''}${Math.floor(remaining % 3600 / 60).toString().padStart(2, '0')}:${(remaining % 60).toString().padStart(2, '0')}`;
        timer.classList.toggle('is-low', !a.submitted && remaining !== null && remaining <= 300);
        if (!a.submitted && remaining === 0) submit(true);
    }

    function submit(expired = false) {
        if (view.attempt.submitted) return;
        trackQuestionTime();
        view.attempt.submitted = true;
        view.attempt.expired = expired;
        finishNativeFeedback();
    }

    function finishNativeFeedback() {
        if (view.session === currentSessionId && nativeIndices(view.items).some(index => !Number.isInteger(index))) {
            view.bankKeys.filter(key => key !== 'current-quiz').forEach(key => activeQuestionSetKeys.add(key));
            rebuildActiveQuestions(false);
            updateSourceDropdown();
        }
        const sessionChangedNotice = 'The session or questions changed. Close Exam mode and reopen the current quizzes.';
        // Check the session before projecting answers, so a changed session is never written to.
        if (view.session !== currentSessionId) {
            view.root.querySelector('[data-exam-notice]').textContent = sessionChangedNotice;
            return;
        }
        // Answers are projected into practice once, on first submission. Reopening a submitted
        // attempt is review only, so later practice answers must survive it.
        const synced = view.attempt.nativeSubmitted
            ? nativeIndices(view.items).every(index => Number.isInteger(index))
            : syncNativeAnswers(view.items, view.attempt.rows);
        if (!synced) {
            view.root.querySelector('[data-exam-notice]').textContent = sessionChangedNotice;
            return;
        }
        filteredIndices = nativeIndices(view.items);
        currentQIndex = 0;
        isAdaptiveReviewMode = false;
        isFilterActive = true;
        syncAdaptiveModeUI();
        resetQuizCompletionCohort('exam-feedback');
        els.filterBanner.style.display = 'flex';
        const banner = getFilterBannerTextEl();
        if (banner) banner.textContent = `Exam questions: ${view.items.length}`;
        setAdaptiveSnapshotButtonVisible(false);
        setAdaptiveSkipUndoneButtonVisible(false);
        if (!view.attempt.nativeSubmitted) {
            view.attempt.rows.forEach((row, i) => {
                if (row.selected === null) return;
                const index = filteredIndices[i];
                // Without a confirmed key there is nothing to score, so no stats or SRS entry.
                if (questions[index]?.answerUnverified) return;
                const correct = row.selected === questions[index].a;
                void globalThis.memoraGameStudy?.creditQuestion('exam', questions[index], correct);
                updateStats(index, correct, Number(row.duration) || 0);
                updateSRS(index, correct);
                if (!correct && autoSaveWrong) savedQuestions[index] = savedQuestions[index] || Date.now();
            });
            view.attempt.nativeSubmitted = true;
            persistNativeProgress();
            flushPendingSessionSave();
        }
        // A stable attempt id lets results pay the completion bonus once per submitted exam.
        if (!view.attempt.attemptId) view.attempt.attemptId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
        save();
        // Capture everything Results needs first: closing clears `view`.
        const { expired, attemptId } = view.attempt;
        const answers = {};
        view.attempt.rows.forEach((row, i) => { if (row.selected !== null) answers[filteredIndices[i]] = row.selected; });
        requestClose();
        showResults({ exam: true, answers, attemptId });
        if (expired) showPopup('Time is up. Your exam answers have been submitted.', null, false);
    }

    function confirm(show) {
        const modal = view.root.querySelector('.exam-submit-confirm');
        modal.hidden = !show;
        ['.exam-body', '.exam-header', '.exam-footer'].forEach((selector) => { view.root.querySelector(selector).inert = show; });
        if (show) {
            const unanswered = view.attempt.rows.filter((r) => r.selected === null).length;
            modal.querySelector('[data-exam-submit-count]').textContent = `${unanswered} unanswered question${unanswered === 1 ? '' : 's'}. You can review answers after submission.`;
            modal.querySelector('button').focus();
        } else view.root.querySelector('[data-exam-heading]').focus();
    }

    function onClick(event) {
        const button = event.target.closest('button');
        if (!button || button.disabled) return;
        const action = button.dataset.examAction;
        if (action === 'close') return requestClose();
        if (!view.attempt) return;
        tick(); // Check the absolute deadline before accepting an interaction.
        if (!view) return;
        const row = view.attempt.rows[view.attempt.current];
        if (button.dataset.examJump !== undefined) return navigate(Number(button.dataset.examJump));
        if (button.dataset.examFilter) {
            view.filter = button.dataset.examFilter;
            view.root.querySelectorAll('[data-exam-filter]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
            button.closest('details').open = false;
            render(); return;
        }
        if (action === 'rail-up' || action === 'rail-down') return view.root.querySelector('.exam-numbers').scrollBy({ top: action === 'rail-up' ? -240 : 240 });
        if (action === 'previous') return navigate(view.attempt.current - 1);
        if (action === 'next') return view.attempt.current === view.items.length - 1 ? (view.attempt.submitted ? requestClose() : confirm(true)) : navigate(view.attempt.current + 1);
        if (action === 'notes') {
            const notes = view.root.querySelector('.exam-notes'); notes.hidden = !notes.hidden; button.setAttribute('aria-pressed', String(!notes.hidden));
            if (!notes.hidden) notes.querySelector('textarea').focus();
            button.closest('details').open = false; return;
        }
        if (view.attempt.submitted) return;
        if (action === 'submit') { button.closest('details').open = false; return confirm(true); }
        if (action === 'cancel-submit') return confirm(false);
        if (action === 'confirm-submit') return submit();
        if (action === 'highlight' || action === 'erase') {
            view.tool = view.tool === action ? '' : action;
            view.root.dataset.tool = view.tool;
            ['highlight', 'erase'].forEach((tool) => view.root.querySelector(`[data-exam-action="${tool}"]`).setAttribute('aria-pressed', String(view.tool === tool)));
            button.closest('details').open = false; return;
        }
        if (view.tool || !global.getSelection().isCollapsed) {
            if (action === 'flag') { row.flagged = !row.flagged; render(); }
            return;
        }
        if (button.dataset.examChoice !== undefined) {
            const index = Number(button.dataset.examChoice);
            if (!row.struck.includes(index)) row.selected = row.selected === index ? null : index;
            const item = view.items[view.attempt.current];
            if (!row.firstAttempt && row.selected !== null && item.answer >= 0 && item.answer < item.choices.length) {
                row.firstAttempt = { correct: row.selected === item.answer, date: Date.now() };
            }
        } else if (button.dataset.examStrike !== undefined) {
            const index = Number(button.dataset.examStrike);
            row.struck = row.struck.includes(index) ? row.struck.filter((i) => i !== index) : [...row.struck, index];
            if (row.struck.includes(row.selected)) row.selected = null;
        } else if (action === 'flag') row.flagged = !row.flagged;
        else return;
        // Saving this draft must not replace the student's practice answers.
        render();
        const selector = button.dataset.examChoice !== undefined ? `[data-exam-choice="${button.dataset.examChoice}"]` : button.dataset.examStrike !== undefined ? `[data-exam-strike="${button.dataset.examStrike}"]` : '[data-exam-action="flag"]';
        view.root.querySelector(selector)?.focus({ preventScroll: true });
    }

    function onKey(event) {
        if (!view) return;
        const topModal = global.MemoraModalCoordinator?.getStack().at(-1);
        if (topModal && topModal !== 'exam-mode') return;
        // Other quiz and Voice shortcuts must never answer the native quiz behind this dialog.
        event.stopImmediatePropagation();
        if (event.key === 'Escape') {
            event.preventDefault();
            if (view.attempt && !view.root.querySelector('.exam-submit-confirm').hidden) confirm(false);
            else requestClose();
            return;
        }
        if (event.key === 'Tab') {
            const scope = view.root.querySelector('.exam-submit-confirm:not([hidden])') || view.root;
            const nodes = [...scope.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea, summary')].filter((node) => !node.closest('[inert]') && node.getClientRects().length);
            const index = nodes.indexOf(d.activeElement);
            event.preventDefault();
            nodes[(index + (event.shiftKey ? -1 : 1) + nodes.length) % nodes.length]?.focus();
            return;
        }
        if (!view.attempt || event.target.closest('input, textarea, [contenteditable="true"]') || !view.root.querySelector('.exam-submit-confirm').hidden) return;
        // Ctrl/Cmd/Alt combinations (copy, select all) are browser shortcuts, never answers or moves.
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        tick();
        if (!view) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); navigate(view.attempt.current + (event.key === 'ArrowLeft' ? -1 : 1)); }
        else if (!view.attempt.submitted && /^[a-z1-9]$/i.test(event.key)) {
            const index = /^[1-9]$/.test(event.key) ? Number(event.key) - 1 : event.key.toUpperCase().charCodeAt(0) - 65;
            const button = view.root.querySelector(`[data-exam-choice="${index}"]`);
            if (button && !button.disabled) { event.preventDefault(); button.click(); }
        }
    }

    function restoreHighlights(row) {
        // Work from the end, so wrapping a text node does not shift earlier text offsets.
        [...row.highlights].sort((a, b) => b.start - a.start).forEach((h, index) => {
            const field = [...view.root.querySelectorAll('[data-exam-field]')].find((node) => node.dataset.examField === h.field);
            if (!field || !Number.isInteger(h.start) || !Number.isInteger(h.end) || h.start < 0 || h.end <= h.start) return;
            const walker = d.createTreeWalker(field, NodeFilter.SHOW_TEXT);
            let offset = 0;
            const nodes = [];
            while (walker.nextNode()) { const node = walker.currentNode; nodes.push({ node, start: offset, end: offset + node.length }); offset += node.length; }
            nodes.reverse().forEach(({ node, start, end }) => {
                if (end <= h.start || start >= h.end) return;
                const range = d.createRange(); range.setStart(node, Math.max(0, h.start - start)); range.setEnd(node, Math.min(node.length, h.end - start));
                const mark = d.createElement('mark'); mark.dataset.examHighlight = String(index); mark.dataset.highlightStart = String(h.start); mark.dataset.highlightField = h.field; range.surroundContents(mark);
            });
        });
    }

    function highlightSelection(event) {
        if (!view?.attempt || view.attempt.submitted) return;
        tick();
        if (!view || view.attempt.submitted) return;
        const row = view.attempt.rows[view.attempt.current];
        if (view.tool === 'erase') {
            const mark = event.target.closest('mark[data-exam-highlight]');
            if (mark) { row.highlights = row.highlights.filter((h) => h.start !== Number(mark.dataset.highlightStart) || h.field !== mark.dataset.highlightField); render(); }
            return;
        }
        if (view.tool !== 'highlight') return;
        const selection = global.getSelection();
        if (!selection?.rangeCount || selection.isCollapsed) return;
        const range = selection.getRangeAt(0);
        const field = (range.startContainer.nodeType === 3 ? range.startContainer.parentElement : range.startContainer).closest('[data-exam-field]');
        if (!field || !field.contains(range.endContainer)) return;
        const before = range.cloneRange(); before.selectNodeContents(field); before.setEnd(range.startContainer, range.startOffset);
        const startOffset = before.toString().length;
        row.highlights.push({ field: field.dataset.examField, start: startOffset, end: startOffset + range.toString().length });
        selection.removeAllRanges();
        render();
    }

    global.MemoraExamMode = Object.freeze({ configure, close: requestClose });
})(window);
